import { UISubscriptionStatus } from "common/types";
import { Optional } from "common/utils/types";

import Banner from "client/common/components/content/Banner";
import { ModalStatus } from "client/common/types";
import { urls } from "client/routes";
import { useFeatures } from "client/clinics/hooks";

import { SubscriptionAddressIssue, SubscriptionForm, SubscriptionPaymentMethod } from "../types";
import { useIsSubscriptionRefreshing } from "../hooks";
import { useSubscriptionClientInfoUpdateDrawer } from "../stackables";
import {
  getSubscriptionAddressIssueContent,
  getSubscriptionClientInfoForms,
  getSubscriptionPaymentIssueContent,
  shouldShowSubscriptionAddressAlert,
  shouldShowSubscriptionClientInfoAlert,
  shouldShowSubscriptionPaymentAlert,
  SubscriptionClientInfoSubject,
  subscriptionClientInfoIssueContent,
} from "../utils";

type SubscriptionIssueBannerProps = {
  subscription: {
    id: number;
    visible_status: UISubscriptionStatus;
    payment_method: Optional<SubscriptionPaymentMethod>;
    shipping_address: Optional<SubscriptionAddressIssue>;
  };
  className?: string;
};

type SubscriptionPaymentBannerProps = {
  subscription: {
    id: number;
    visible_status: UISubscriptionStatus;
    payment_method: Optional<SubscriptionPaymentMethod>;
  };
  className?: string;
};

/**
 * Announces a payment problem on the Autoship itself, so a client sees it without opening
 * one of the forms it blocks. Renders nothing when there is no problem to announce.
 *
 * Routed to the payment form rather than opening it in place so the list and the detail page
 * share one implementation.
 */
const SubscriptionPaymentBanner = ({ subscription, className }: SubscriptionPaymentBannerProps) => {
  if (!shouldShowSubscriptionPaymentAlert(subscription)) {
    return null;
  }

  const { bannerMessage, actionLabel } = getSubscriptionPaymentIssueContent(
    subscription.payment_method,
  );

  return (
    <Banner inline={true} variant="danger" className={className}>
      <Banner.Text
        label={bannerMessage}
        actions={[
          {
            children: actionLabel,
            dest: urls.subscriptions.detail({
              params: { id: subscription.id },
              search: {
                form: SubscriptionForm.UpdatePayment,
                visible: ModalStatus.Open,
              },
            }),
          },
        ]}
      />
    </Banner>
  );
};

type SubscriptionAddressBannerProps = {
  subscription: {
    id: number;
    visible_status: UISubscriptionStatus;
    shipping_address: Optional<SubscriptionAddressIssue>;
  };
  className?: string;
};

/**
 * Announces a shipping address problem on the Autoship itself, so a client sees it without
 * opening one of the forms it blocks. Renders nothing when there is no problem to announce.
 *
 * Routed to the address form rather than opening it in place so the list and the detail page
 * share one implementation.
 */
const SubscriptionAddressBanner = ({ subscription, className }: SubscriptionAddressBannerProps) => {
  if (!shouldShowSubscriptionAddressAlert(subscription)) {
    return null;
  }

  const { bannerMessage, actionLabel } = getSubscriptionAddressIssueContent(
    subscription.shipping_address,
  );

  return (
    <Banner inline={true} variant="danger" className={className}>
      <Banner.Text
        label={bannerMessage}
        actions={[
          {
            children: actionLabel,
            dest: urls.subscriptions.detail({
              params: { id: subscription.id },
              search: {
                form: SubscriptionForm.UpdateAddress,
                visible: ModalStatus.Open,
              },
            }),
          },
        ]}
      />
    </Banner>
  );
};

type SubscriptionClientInfoBannerProps = {
  subscription: SubscriptionClientInfoSubject & { visible_status: UISubscriptionStatus };
  className?: string;
};

/**
 * Announces missing client information on the Autoship itself, with one action per account form
 * the client needs. Renders nothing when there is none to announce.
 *
 * Opens each form in place, since the account drawers live outside the Autoship's routes.
 */
const SubscriptionClientInfoBanner = ({
  subscription,
  className,
}: SubscriptionClientInfoBannerProps) => {
  const { open: openClientInfoUpdate } = useSubscriptionClientInfoUpdateDrawer();
  const isGranularControlsEnabled = useFeatures().CONTROLLED_SUBSTANCE_GRANULAR_CONTROLS;
  const isRefreshing = useIsSubscriptionRefreshing(subscription.id);

  if (!shouldShowSubscriptionClientInfoAlert(subscription, isGranularControlsEnabled)) {
    return null;
  }

  const { bannerMessage, actionLabels } = subscriptionClientInfoIssueContent;

  return (
    <Banner inline={true} variant="danger" className={className}>
      <Banner.Text
        label={bannerMessage}
        areActionsLoading={isRefreshing}
        actions={getSubscriptionClientInfoForms(subscription, isGranularControlsEnabled).map(
          (form) => ({
            children: actionLabels[form],
            onClick: () => openClientInfoUpdate(form, subscription),
          }),
        )}
      />
    </Banner>
  );
};

const SubscriptionIssueBanner = ({ subscription, className }: SubscriptionIssueBannerProps) => {
  if (shouldShowSubscriptionPaymentAlert(subscription)) {
    return <SubscriptionPaymentBanner subscription={subscription} className={className} />;
  }

  return <SubscriptionAddressBanner subscription={subscription} className={className} />;
};

export default Object.assign(SubscriptionIssueBanner, {
  ClientInfo: SubscriptionClientInfoBanner,
});
